import { createNode } from '@glueapp/graph-types';
import { DataType } from '@glueapp/graph-types';

const VariableValuePortType: DataType = {
    kind: 'generic',
    identifier: 'T',
};

export const SetVariable = createNode({
    id: 'Set Variable',
    name: 'Set Variable',
    description: 'A block to create or set variables with',
    aiDescription: [
        'Sets a graph variable to a new value. Variables persist across execution steps.',
        'Setup:',
        '1. Add the node: glue add "Set Variable"',
        '2. Select which variable to write to using state:',
        '   glue set-state <id> selectedVariable "myVariableName"',
        "3. Run glue shape <id> to see the resolved port types (based on the variable's type).",
        '4. Connect a value to the "Variable Value" input port.',
        '',
        'computeShape behavior:',
        '- When selectedVariable is set and the variable has a resolved type, the "Variable Value" input',
        '  port and "Pass Through" output port are both typed to match the variable\'s type.',
        '- If the variable has no type yet, connecting a typed value to "Variable Value" will infer and',
        "  set the variable's type (type flows from connection into the variable definition).",
        '- Without a selected variable, ports remain generic (unresolved).',
        '',
        'Output: "Pass Through" emits the same value that was written, allowing chaining.',
        '',
        'Example:',
        '  glue add "Set Variable"  # returns <id>',
        '  glue set-state <id> selectedVariable "counter"',
        "  glue shape <id>  # shows ports typed to the variable's type",
        '  glue connect <number-node>:Output <id>:"Variable Value"',
    ].join('\n'),
    category: 'Variables',
    color: '#7d898f',
    integration: undefined,
    inPorts: {
        'Variable Value': VariableValuePortType,
    },
    computeShape: input => {
        if (input.selectedVariable) {
            const resolvedVariableType = input.selectedVariable.resolvedType;
            if (resolvedVariableType) {
                const resolvedVariableValueType: DataType = {
                    ...VariableValuePortType,
                    resolvedType: resolvedVariableType,
                };
                return {
                    inPorts: input.nodeInPorts.map(port =>
                        port.name === 'Variable Value' ? { name: 'Variable Value', type: resolvedVariableValueType } : port,
                    ),
                    outPorts: input.nodeOutPorts.map(port =>
                        port.name === 'Pass Through' ? { name: 'Pass Through', type: resolvedVariableValueType } : port,
                    ),
                };
            }

            const VariableInPort = input.preResolvedInputPorts.find(inPort => inPort.name === 'Variable Value');
            if (VariableInPort && VariableInPort.type && VariableInPort.type.kind === 'generic' && VariableInPort.type.resolvedType) {
                input.setVariableType(input.selectedVariable.name, VariableInPort.type.resolvedType);

                const resolvedVariableValueType: DataType = {
                    ...VariableValuePortType,
                    resolvedType: VariableInPort.type.resolvedType,
                };

                return {
                    inPorts: input.nodeInPorts.map(port =>
                        port.name === 'Variable Value' ? { name: 'Variable Value', type: resolvedVariableValueType } : port,
                    ),
                    outPorts: input.nodeOutPorts.map(port =>
                        port.name === 'Pass Through' ? { name: 'Pass Through', type: resolvedVariableValueType } : port,
                    ),
                };
            }
        }
        return {
            inPorts: input.nodeInPorts,
            outPorts: input.nodeOutPorts,
        };
    },
    outPorts: {
        'Pass Through': {
            kind: 'generic',
            identifier: 'T',
        },
    },
    execute: function (input) {
        const inputValue = input.inPorts['Variable Value'];
        if (inputValue !== undefined) {
            input.setVariableValue(inputValue);
        }
        return { output: { Trigger: { isTriggerValue: true, running: true }, 'Pass Through': inputValue } };
    },
});
